/**
 * .gbox file format - Gotti Sandbox Project Format
 * 
 * Re-exports types from @gotti/sandbox-shared and adds local validation utilities.
 * 
 * @fileoverview Gotti Sandbox .gbox file format definition
 */

// Re-export all types from the shared package
export {
  GBOX_FORMAT_VERSION,
  GBOX_EXTENSION,
  GBOX_MIME_TYPE,
  DEFAULT_CLIENT_SETTINGS,
  type GboxFile,
  type GboxFiles,
  type GboxConfig,
  type GboxMeta,
  type GboxDocument,
  type GboxClient,
  type GboxClientSettings,
  createEmptyGboxFiles,
  createGboxDocument,
  applyClientDefaults,
} from '@gotti/sandbox-shared';

import { GBOX_FORMAT_VERSION, type GboxDocument } from '@gotti/sandbox-shared';

/**
 * Result of validating a .gbox file
 */
export interface GboxValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  /** The parsed document if valid */
  document?: GboxDocument;
}

/**
 * Validates a parsed .gbox document
 */
export function validateGboxDocument(data: unknown): GboxValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Invalid .gbox file: not an object'], warnings };
  }

  const doc = data as Record<string, unknown>;

  // Check version
  if (typeof doc.version !== 'number') {
    errors.push('Missing or invalid version field');
  } else if (doc.version > GBOX_FORMAT_VERSION) {
    warnings.push(`File was created with a newer format version (${doc.version}). Some features may not work.`);
  }

  // Check meta
  if (!doc.meta || typeof doc.meta !== 'object') {
    errors.push('Missing or invalid meta section');
  } else {
    const meta = doc.meta as Record<string, unknown>;
    if (typeof meta.name !== 'string') {
      errors.push('Missing or invalid meta.name');
    }
    if (typeof meta.exportedAt !== 'string') {
      warnings.push('Missing exportedAt timestamp');
    }
  }

  // Check files
  if (!doc.files || typeof doc.files !== 'object') {
    errors.push('Missing or invalid files section');
  } else {
    const files = doc.files as Record<string, unknown>;
    
    // Validate client folder
    if (!files.client || typeof files.client !== 'object') {
      errors.push('Missing or invalid files.client');
    }
    
    // Validate server folder
    if (!files.server || typeof files.server !== 'object') {
      errors.push('Missing or invalid files.server');
    }
    
    // Validate shared folder
    if (!files.shared || typeof files.shared !== 'object') {
      errors.push('Missing or invalid files.shared');
    }
  }

  if (errors.length > 0) {
    return { valid: false, errors, warnings };
  }

  return { 
    valid: true, 
    errors: [], 
    warnings, 
    document: doc as unknown as GboxDocument 
  };
}

/**
 * Parse a .gbox file from string content
 */
export function parseGboxFile(content: string): GboxValidationResult {
  try {
    const data = JSON.parse(content);
    return validateGboxDocument(data);
  } catch (err) {
    return {
      valid: false,
      errors: [`Failed to parse .gbox file: ${err instanceof Error ? err.message : 'Unknown error'}`],
      warnings: [],
    };
  }
}
